/**
 * Formateadores y cálculos puros de Gestión Cartera.
 * Las reglas de negocio están documentadas en README.md → "Definiciones de los indicadores".
 */

const MONTHS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const DAY_MS = 24 * 60 * 60 * 1000;

export const ACTIVE_CREDIT_STATUSES = ["APPROVED", "REESTRUCTURADO", "AMPLIADO"];
export const PAID_CREDIT_STATUSES = ["COMPLETED", "CONDONADO"];
const PAID_QUOTA_STATUSES = ["PAID", "CONDONADA"];
const OPEN_QUOTA_STATUSES = ["PENDING", "OVERDUE", "PARTIALLY_PAID"];

export const MORA_FILTERS = [
  { value: "todos", label: "Todos" },
  { value: "aldia", label: "Al día" },
  { value: "mora", label: "En mora" },
  { value: "m1", label: "Mora 1–30" },
  { value: "m2", label: "Mora 31–90" },
  { value: "m3", label: "Mora > 90" },
];

/** 0 es un saldo válido. null, undefined, vacío y NaN no lo son. */
export const isMoneyAmount = (value) =>
  value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));

export const formatMoney = (value) => {
  if (!isMoneyAmount(value)) return "—";
  const rounded = Math.round(Number(value));
  const sign = rounded < 0 ? "-" : "";
  return `${sign}$${String(Math.abs(rounded)).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
};

/**
 * Porcentaje con coma decimal (máx. 2 decimales). `isFraction`: el backend guarda tasas como
 * fracción (interestRate 0.0186 = 1,86 %; tarifaFga 0.2 = 20 %); commissionPercentage ya viene en %.
 */
export const formatPercent = (value, { isFraction = true } = {}) => {
  if (value === null || value === undefined || value === "") return "—";
  const percent = Number(value) * (isFraction ? 100 : 1);
  return `${String(Math.round(percent * 100) / 100).replace(".", ",")} %`;
};

export const formatYesNo = (value) => {
  if (value === null || value === undefined) return "—";
  return value ? "Sí" : "No";
};

const splitIsoDate = (isoDate) => {
  if (!isoDate) return null;
  const [year, month, day] = String(isoDate).slice(0, 10).split("-");
  if (!year || !month || !day) return null;
  return { year, month, day };
};

/** Recibe "YYYY-MM-DD" o "YYYY-MM-DDTHH:mm..." y devuelve "05 oct 2026" (sin desfase de zona horaria). */
export const formatDate = (isoDate) => {
  const parts = splitIsoDate(isoDate);
  if (!parts) return "—";
  return `${parts.day} ${MONTHS[Number(parts.month) - 1]} ${parts.year}`;
};

/** Misma fecha, en "05/10/2026", para tablas donde el mes abreviado no cabe. */
export const formatNumericDate = (isoDate) => {
  const parts = splitIsoDate(isoDate);
  if (!parts) return "—";
  return `${parts.day}/${parts.month}/${parts.year}`;
};

/** Día calendario en Colombia. No usa la zona del navegador. */
export const todayIso = (now = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Bogota",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const value = (type) => parts.find((part) => part.type === type).value;
  return `${value("year")}-${value("month")}-${value("day")}`;
};

const toUtcDay = (isoDate) => Date.parse(`${String(isoDate).slice(0, 10)}T00:00:00Z`);

export const daysBetween = (fromIso, toIso) =>
  Math.round((toUtcDay(toIso) - toUtcDay(fromIso)) / DAY_MS);

export const formatDocument = (person) =>
  [person?.documentType, person?.documentNumber].filter(Boolean).join(" ");

/** Nombre visible: name + lastName. fullName solo si esos dos vienen vacíos. */
export const formatFullName = (person) => {
  const composed = [person?.name, person?.lastName]
    .map((part) => String(part ?? "").replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join(" ");
  if (composed) return composed;
  const fullName = String(person?.fullName ?? "").replace(/\s+/g, " ").trim();
  return fullName || "—";
};

export const getInitials = (fullName) =>
  String(fullName)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

export const moraBadge = (days) => {
  if (!days) return { tone: "green", label: "0 días" };
  const label = `${days} ${days === 1 ? "día" : "días"}`;
  if (days <= 30) return { tone: "yellow", label };
  if (days <= 90) return { tone: "orange", label };
  return { tone: "red", label };
};

export const matchesMoraFilter = (days, filter) => {
  switch (filter) {
    case "aldia":
      return days === 0;
    case "mora":
      return days > 0;
    case "m1":
      return days >= 1 && days <= 30;
    case "m2":
      return days >= 31 && days <= 90;
    case "m3":
      return days > 90;
    default:
      return true;
  }
};

export const sortByMoraDays = (rows, order) =>
  [...rows].sort((a, b) => (order === "asc" ? a.moraDays - b.moraDays : b.moraDays - a.moraDays));

export const isQuotaPaid = (quota) => PAID_QUOTA_STATUSES.includes(quota?.status);

export const isQuotaOpen = (quota) => OPEN_QUOTA_STATUSES.includes(quota?.status);

export const quotaBalance = (quota) =>
  isQuotaOpen(quota) ? Math.max((quota.value || 0) - (quota.paidValue || 0), 0) : 0;

export const isQuotaOverdue = (quota, today) =>
  isQuotaOpen(quota) && String(quota.payDate).slice(0, 10) < today;

export const quotaState = (quota, today) => {
  if (isQuotaPaid(quota)) return { tone: "green", label: "Pagada" };
  if (isQuotaOverdue(quota, today)) return { tone: "red", label: "Vencida" };
  if (isQuotaOpen(quota)) return { tone: "blue", label: "Pendiente" };
  if (quota?.status === "REESTRUCTURADA") return { tone: "purple", label: "Reestructurada" };
  if (quota?.status === "AMPLIADA") return { tone: "purple", label: "Ampliada" };
  return { tone: "gray", label: quota?.status || "—" };
};

/** Capital de la cuota = amortización; intereses = valor de cuota − amortización. */
export const quotaCapital = (quota) => quota?.amortizacion || 0;

export const quotaInterest = (quota) => Math.max((quota?.value || 0) - quotaCapital(quota), 0);

/** Valor de cuota + mora generada + GAC generado. Si falta un monto, no se suma un cero inventado. */
export const quotaChargedTotal = (quota, gac) => {
  const parts = [quota?.value, quota?.interesesMoratoriosGenerados, gac];
  if (parts.some((part) => !isMoneyAmount(part))) return null;
  return parts.reduce((sum, part) => sum + Number(part), 0);
};

export const isCreditActive = (credit) =>
  ACTIVE_CREDIT_STATUSES.includes(credit?.status) && (credit?.quotas || []).some(isQuotaOpen);

const CREDIT_STATUS_LABELS = {
  PENDING: "Pendiente",
  DECLINED: "Rechazado",
  TEMPORAL: "Temporal",
  DOCUMENTOS_PENDIENTES: "Documentos pendientes",
  DOCUMENTOS_CARGADOS: "Documentos cargados",
  FGA: "FGA",
  FRAUDE: "Fraude",
  REESTRUCTURADO: "Reestructurado",
  AMPLIADO: "Ampliado",
};

/** Resumen de un crédito con sus cuotas (CreditWithQuotas). */
export const summarizeCredit = (credit, today) => {
  const quotas = credit?.quotas || [];
  const overdueQuotas = quotas.filter((quota) => isQuotaOverdue(quota, today));
  const oldestOverdue = overdueQuotas[0] || null;
  const active = isCreditActive(credit);
  const inMora = active && overdueQuotas.length > 0;

  return {
    active,
    inMora,
    totalPending: quotas.reduce((sum, quota) => sum + quotaBalance(quota), 0),
    overdueValue: overdueQuotas.reduce(
      (sum, quota) => sum + quotaBalance(quota) + (quota.interesesMoratoriosGenerados || 0),
      0
    ),
    overdueCount: overdueQuotas.length,
    oldestOverdue,
    delayDays: oldestOverdue ? daysBetween(oldestOverdue.payDate, today) : 0,
    paidCount: quotas.filter(isQuotaPaid).length,
    pendingCount: quotas.filter((quota) => isQuotaOpen(quota) && !isQuotaOverdue(quota, today)).length,
    quotasCount: quotas.length,
    state: creditState(credit, active, inMora),
  };
};

const creditState = (credit, active, inMora) => {
  if (inMora) return { tone: "red", label: "En mora" };
  if (active) return { tone: "blue", label: "Activo" };
  if (PAID_CREDIT_STATUSES.includes(credit?.status)) return { tone: "green", label: "Pagado" };
  return { tone: "gray", label: CREDIT_STATUS_LABELS[credit?.status] || credit?.status || "—" };
};

/** Indicadores del perfil: créditos activos y suma de su balanceCredit (saldo insoluto). */
export const summarizeClientCredits = (credits, today) => {
  const active = credits
    .map((credit) => ({ credit, summary: summarizeCredit(credit, today) }))
    .filter((item) => item.summary.active);
  const balances = active.map((item) => item.credit.balanceCredit);
  return {
    activeCount: active.length,
    activeInMora: active.filter((item) => item.summary.inMora).length,
    totalPending: balances.every(isMoneyAmount)
      ? balances.reduce((sum, amount) => sum + Number(amount), 0)
      : null,
  };
};

/**
 * Componentes de una cuota, en el orden en que se muestran.
 * `pendingKey`: campo de QuotaPendingBreakdownDto (GET /v1/condonations/quotas/{uuid}/pending).
 * `paidKey`: campo de la respuesta de GET /v1/quotas/{uuid}/paid-components.
 */
export const QUOTA_COMPONENTS = [
  { label: "Capital", pendingKey: "pendingCapital", paidKey: "capital" },
  { label: "Interés corriente", pendingKey: "pendingInterest", paidKey: "currentInterest" },
  { label: "Intereses de mora", pendingKey: "pendingMora", paidKey: "lateInterest" },
  { label: "GAC", pendingKey: "pendingGac", paidKey: "gac" },
  { label: "FGA", pendingKey: "pendingFga", paidKey: "fga" },
  { label: "Seguro de vida", pendingKey: "pendingLifeInsurance", paidKey: "lifeInsurance" },
  { label: "Seguro de desempleo", pendingKey: "pendingUnemploymentInsurance", paidKey: "unemploymentInsurance" },
];

const sumComponents = (rows) => rows.reduce((sum, row) => sum + row.value, 0);

/** Filas { label, value } de un desglose, según la llave indicada ("pendingKey" o "paidKey"). */
export const toComponentRows = (breakdown, keyName) => {
  const rows = QUOTA_COMPONENTS.map((component) => ({
    label: component.label,
    value: Number(breakdown?.[component[keyName]]) || 0,
  }));
  return { rows, total: sumComponents(rows) };
};

/**
 * Qué secciones muestra el modal de componentes:
 * - pagados: cuotas con algún pago aplicado (pagada, condonada, parcial, reestructurada, ampliada).
 * - proyectados: cuotas que todavía tienen saldo (pendiente, vencida, parcial).
 */
export const quotaComponentSections = (quota) => ({
  showPaid: !isQuotaOpen(quota) || quota?.status === "PARTIALLY_PAID",
  showProjected: isQuotaOpen(quota),
});

/**
 * Columnas del plan de pagos original. Las fijas siempre se ven; las demás se pueden ocultar.
 * La mora y el GAC no van aquí: no se pueden proyectar en la originación, solo nacen con atraso.
 * `total`: la columna suma en la fila "Total del plan".
 */
export const ORIGINAL_PLAN_COLUMNS = [
  { key: "quotaNumber", label: "N.º de cuota", fixed: true, type: "quota" },
  { key: "estimatedPayDate", label: "Fecha de vencimiento", fixed: true, type: "date" },
  { key: "estimatedQuotaValue", label: "Valor de cuota estimado", fixed: true, type: "money", total: true },
  { key: "capital", label: "Amortización esperada", defaultVisible: true, type: "money", total: true },
  { key: "currentInterest", label: "Interés corriente", defaultVisible: true, type: "money", total: true },
  { key: "fga", label: "FGA", defaultVisible: true, type: "money", total: true },
  { key: "lifeInsurance", label: "Seguro de vida", defaultVisible: true, type: "money", total: true },
  { key: "unemploymentInsurance", label: "Seguro de desempleo", defaultVisible: true, type: "money", total: true },
  { key: "projectedCapitalBalance", label: "Saldo capital proyectado", defaultVisible: true, type: "money" },
];

export const defaultOriginalPlanColumns = () =>
  ORIGINAL_PLAN_COLUMNS.filter((column) => !column.fixed && column.defaultVisible).map((column) => column.key);

export const visibleOriginalPlanColumns = (selectedKeys) =>
  ORIGINAL_PLAN_COLUMNS.filter((column) => column.fixed || selectedKeys.includes(column.key));

/** Desglose del plan original de una sola cuota. Sin mora ni GAC: no se proyectan en la originación. */
export const ORIGINAL_QUOTA_FIELDS = [
  { key: "estimatedPayDate", label: "Vencimiento estimado", type: "date" },
  { key: "estimatedQuotaValue", label: "Valor de cuota estimado", type: "money" },
  { key: "capital", label: "Amortización esperada", type: "money" },
  { key: "currentInterest", label: "Interés corriente", type: "money" },
  { key: "fga", label: "FGA", type: "money" },
  { key: "lifeInsurance", label: "Seguro de vida", type: "money" },
  { key: "unemploymentInsurance", label: "Seguro de desempleo", type: "money" },
  { key: "projectedCapitalBalance", label: "Saldo capital proyectado", type: "money" },
];

export const originalPlanQuota = (plan, quotaNumber) =>
  (plan?.quotas || []).find((row) => Number(row.quotaNumber) === Number(quotaNumber)) || null;

/**
 * Mismo concepto, misma fila, para comparar originación contra aplicación.
 * Mora y GAC no se proyectan en la originación: a la izquierda va lo generado y a la derecha lo pagado.
 * FGA, vida y desempleo aplicados vienen del desglose de componentes pagados.
 */
export const QUOTA_COMPARISON_FIELDS = [
  { originalLabel: "Fecha de vencimiento", actualLabel: "Fecha de pago", type: "date", originalKey: "estimatedPayDate", actualFrom: "quota", actualKey: "paidDate" },
  { originalLabel: "Valor de cuota estimado", actualLabel: "Valor pagado", type: "money", originalKey: "estimatedQuotaValue", actualFrom: "quota", actualKey: "paidValue" },
  { originalLabel: "Capital esperado (Amor. esperada)", actualLabel: "Capital pagado (Amor. pagada)", type: "money", originalKey: "capital", actualFrom: "quota", actualKey: "amortizacion" },
  { originalLabel: "Interés corriente", actualLabel: "Intereses corrientes", type: "money", originalKey: "currentInterest", actualFrom: "quota", actualKey: "interesesPagados" },
  { originalLabel: "FGA", actualLabel: "FGA", type: "money", originalKey: "fga", actualFrom: "paid", actualKey: "fga" },
  { originalLabel: "Seguro de vida", actualLabel: "Seguro de vida", type: "money", originalKey: "lifeInsurance", actualFrom: "paid", actualKey: "lifeInsurance" },
  { originalLabel: "Seguro de desempleo", actualLabel: "Seguro de desempleo", type: "money", originalKey: "unemploymentInsurance", actualFrom: "paid", actualKey: "unemploymentInsurance" },
  { originalLabel: "Saldo capital proyectado", actualLabel: "Saldo capital", type: "money", originalKey: "projectedCapitalBalance", actualFrom: "quota", actualKey: "saldoCapital" },
  { originalLabel: "Interés moratorio", actualLabel: "Interés moratorio", type: "money", originalFrom: "quota", originalKey: "interesesMoratoriosGenerados", actualFrom: "paid", actualKey: "lateInterest" },
  { originalLabel: "GAC", actualLabel: "GAC", type: "money", originalFrom: "quota", originalKey: "generatedGac", actualFrom: "paid", actualKey: "gac" },
  { actualLabel: "Condonación", type: "money", actualFrom: "quota", actualKey: "condonacion", actualOnly: true, bold: true },
  { actualLabel: "Sobrepago (Overpayment)", type: "money", actualFrom: "quota", actualKey: "overpayment", actualOnly: true, bold: true },
];

const comparisonAmount = (value) => (isMoneyAmount(value) ? Number(value) : null);

export const quotaComparisonRows = (originalQuota, quota, paidBreakdown) =>
  QUOTA_COMPARISON_FIELDS.map((field) => {
    let actualValue = null;
    if (field.actualFrom === "paid") {
      actualValue = paidBreakdown ? comparisonAmount(paidBreakdown[field.actualKey]) : null;
    } else if (field.type === "date") {
      actualValue = quota?.[field.actualKey] || null;
    } else {
      actualValue = comparisonAmount(quota?.[field.actualKey]);
    }
    let originalValue = null;
    if (field.originalFrom === "quota") {
      originalValue = comparisonAmount(quota?.[field.originalKey]);
    } else if (field.originalKey != null) {
      originalValue = field.type === "date"
        ? originalQuota?.[field.originalKey] || null
        : comparisonAmount(originalQuota?.[field.originalKey]);
    }
    return {
      originalLabel: field.originalLabel,
      actualLabel: field.actualLabel,
      type: field.type,
      originalValue,
      actualValue,
      actualOnly: Boolean(field.actualOnly),
      bold: Boolean(field.bold),
    };
  });

/** Pagos cuyo payment record está aplicado. Sin estado, el listado de la cuota ya trae solo los aplicados. */
export const appliedPaymentRecords = (payments) =>
  (payments || []).filter((payment) => {
    const status = payment?.paymentRecord?.status || payment?.status;
    return status == null || status === "" || status === "APPLIED";
  });

const firstFilled = (...values) => values.find((value) => String(value ?? "").trim()) ?? "";

/** oldId, collector y notas de un pago aplicado. `record` es el payment record del historial, si existe. */
export const appliedPaymentDetails = (payment, record) => {
  const nested = payment?.paymentRecord;
  const oldId = record?.oldId ?? nested?.oldId ?? payment?.oldId ?? payment?.paymentRecordOldId ?? null;
  return {
    oldId: oldId == null || oldId === "" ? null : oldId,
    collector: String(firstFilled(record?.paymentCollector, payment?.paymentCollector, nested?.paymentCollector)).trim(),
    notes: String(firstFilled(record?.paymentNotes, payment?.paymentNotes, nested?.paymentNotes, payment?.notes)).trim(),
  };
};

export const sumColumn = (rows, key) => rows.reduce((sum, row) => sum + (Number(row[key]) || 0), 0);

/** Crédito con la originación más reciente. Sin fecha, gana el oldId más alto. */
export const latestOriginatedCredit = (credits) => {
  const list = (credits || []).filter(Boolean);
  if (!list.length) return null;
  const timeOf = (credit) => {
    const time = Date.parse(String(credit.originDate || "").slice(0, 10));
    return Number.isNaN(time) ? null : time;
  };
  return list.reduce((latest, credit) => {
    const latestTime = timeOf(latest);
    const creditTime = timeOf(credit);
    if (latestTime == null && creditTime == null) return Number(credit.oldId) > Number(latest.oldId) ? credit : latest;
    if (latestTime == null) return credit;
    if (creditTime == null) return latest;
    if (creditTime !== latestTime) return creditTime > latestTime ? credit : latest;
    return Number(credit.oldId) > Number(latest.oldId) ? credit : latest;
  });
};

/** Días de pago permitidos: ±10 días alrededor del día actual (misma regla que Cobranza). */
export const allowedPayDays = (currentDay) =>
  Array.from({ length: 30 }, (_, index) => index + 1).filter(
    (day) => Math.abs(currentDay - day) <= 10 || Math.abs(currentDay + 30 - day) <= 10
  );
